import { useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import { Plus, MoreHorizontal } from 'lucide-react';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { useQuotations } from '@/composables/financial/useQuotations';
import { useDebounce } from '@/composables/shared/useDebounce';
import { openPdfInNewTab } from '@/lib/pdfViewer';
import { PageContainer } from '@/components/layout/PageContainer';
import { DataTable } from '@/components/data-table/DataTable';
import { DataTableToolbar } from '@/components/data-table/DataTableToolbar';
import { DataTableColumnHeader } from '@/components/data-table/DataTableColumnHeader';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { StatusBadge } from '@/components/StatusBadge';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { formatDate, formatCurrency } from '@/lib/formatters';
import { useState, useMemo } from 'react';
import type { Quotation } from '@/types/table';

/** A SENT quotation past its validUntil date is shown as EXPIRED in the UI (server status is left as-is). */
function displayStatus(q: Quotation): string {
  if (q.status === 'SENT' && q.validUntil && new Date(q.validUntil).getTime() < Date.now()) return 'EXPIRED';
  return q.status;
}

export function QuotationListPage() {
  const navigate = useNavigate();
  const { quotations, meta, isLoading, filters, sendQuotation, duplicateQuotation, deleteQuotation, downloadPdf } = useQuotations();
  const [search, setSearch] = useState(filters.current.search);
  const debouncedSearch = useDebounce(search, 300);
  if (debouncedSearch !== filters.current.search) filters.set({ search: debouncedSearch });

  const [deleteTarget, setDeleteTarget] = useState<Quotation | null>(null);

  const handleDownloadPdf = async (q: Quotation) => {
    try {
      const blob = await downloadPdf(q.id);
      saveAs(blob, `quotation-${q.quotationNumber || q.id}.pdf`);
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const handleViewPdf = async (q: Quotation) => {
    try {
      await openPdfInNewTab(() => downloadPdf(q.id));
    } catch {
      toast.error('Failed to open PDF');
    }
  };

  const columns: ColumnDef<Quotation, unknown>[] = useMemo(() => [
    { accessorKey: 'quotationNumber', header: ({ column }) => <DataTableColumnHeader column={column} title="Quotation #" />, cell: ({ row }) => <span className="font-medium">{row.original.quotationNumber}</span> },
    { accessorKey: 'billingName', header: 'Client', cell: ({ row }) => row.original.billingName || '-' },
    { accessorKey: 'totalAmount', header: ({ column }) => <DataTableColumnHeader column={column} title="Total" />, cell: ({ row }) => formatCurrency(row.original.totalAmount, row.original.currency) },
    { accessorKey: 'status', header: 'Status', cell: ({ row }) => <StatusBadge status={displayStatus(row.original)} type="quotation" /> },
    {
      id: 'invoiced',
      header: 'Invoiced',
      cell: ({ row }) => {
        const invoiced = Number(row.original.invoicedAmount) || 0;
        if (invoiced <= 0) return <span className="text-muted-foreground">-</span>;
        const total = Number(row.original.totalAmount) || 0;
        return <span title={`Paid ${formatCurrency(row.original.paidAmount ?? 0, row.original.currency)}`}>{total > 0 ? Math.min(100, Math.round((invoiced / total) * 100)) : 0}%</span>;
      },
    },
    { accessorKey: 'issueDate', header: ({ column }) => <DataTableColumnHeader column={column} title="Issued" />, cell: ({ row }) => formatDate(row.original.issueDate) },
    { accessorKey: 'validUntil', header: ({ column }) => <DataTableColumnHeader column={column} title="Valid Until" />, cell: ({ row }) => formatDate(row.original.validUntil) },
    {
      id: 'actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); sendQuotation.mutate(row.original.id); }}>Send</DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); duplicateQuotation.mutate(row.original.id); }}>Duplicate</DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewPdf(row.original); }}>View PDF</DropdownMenuItem>
            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDownloadPdf(row.original); }}>Download PDF</DropdownMenuItem>
            <DropdownMenuItem className="text-destructive" onClick={(e) => { e.stopPropagation(); setDeleteTarget(row.original); }}>Delete</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], []);

  return (
    <PageContainer title="Quotations" description="Send price offers to clients; accept to convert into invoices" actions={<Button onClick={() => navigate('/financial/quotations/new')}><Plus className="mr-2 h-4 w-4" />New Quotation</Button>}>
      <DataTableToolbar searchValue={search} onSearchChange={setSearch} searchPlaceholder="Search quotations..." />
      <DataTable columns={columns} data={quotations} isLoading={isLoading} page={meta?.page ?? 1} totalPages={meta?.totalPages ?? 1} total={meta?.total ?? 0} pageSize={filters.current.limit} onPageChange={(p) => filters.set({ page: p })} onPageSizeChange={(s) => filters.set({ limit: s })} onRowClick={(row) => navigate(`/financial/quotations/${row.id}`)} />
      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={() => setDeleteTarget(null)}
        title="Delete Quotation"
        description={`Are you sure you want to delete quotation ${deleteTarget?.quotationNumber}? This action cannot be undone.`}
        variant="destructive"
        confirmLabel="Delete"
        onConfirm={() => { if (deleteTarget) { deleteQuotation.mutate(deleteTarget.id); setDeleteTarget(null); } }}
        isLoading={deleteQuotation.isPending}
      />
    </PageContainer>
  );
}
